<template>
  <b-modal
      :visible="visible"
      ref="modal"
      id="admin-category-image-add-modal"
      title="Add New Category Image"
      @hidden="$emit('cancel')"
      hide-footer
      centered
  >
    <b-form @submit.prevent="submitForm">
      <b-form-group label="Category Type">
        <b-form-select
            v-model="formData.category_type"
            :options="categoryTypes"
            :state="fieldValid('category_type')"
        />
        <b-form-invalid-feedback :state="fieldValid('category_type')">
          {{ formValidation.category_type }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Category">
        <b-form-select
            v-model="formData.category"
            :options="categories"
            :state="fieldValid('category')"
            :disabled="!formData.category_type"
        />
        <b-form-invalid-feedback :state="fieldValid('category')">
          {{ formValidation.category }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Image(s)">
        <b-form-file
            v-model="formData.images"
            :state="fieldValid('images')"
            placeholder="Choose an images or drop it here"
            drop-placeholder="Drop image here"
            accept="image/jpeg, image/png"
            multiple
        ></b-form-file>
        <b-form-invalid-feedback :state="fieldValid('images')">
          {{ formValidation.images }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-button type="submit" variant="success" :disabled="formState.processing">
        Submit
        <b-spinner v-if="formState.processing" class="ml-1" small/>
      </b-button>
      <b-btn type="button" variant="secondary" @click.prevent="close">Cancel</b-btn>
    </b-form>
  </b-modal>
</template>

<script>
import {API_ROUTES} from "../api";

export default {
  name: "AdminCategoryImageAddModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        category: "",
        category_type: "",
        images: null
      },
      formValidation: {},
      formState: {
        processing: false,
        sizeExceeds: false
      },
      maxFilesSize: 10
    }
  },
  computed: {
    categoryTypes() {
      return this.$store.state.data.category_types.map(ct => ({
        value: ct.id,
        text: ct.name
      }));
    },
    categories() {
      let categories = this.$store.state.data.categories
          .map(category => ({
            value: category.id,
            text: category.name,
            category_type_id: category.category_type_id
          }));
      if (this.formData.category_type) {
        categories = categories.filter(category => category.category_type_id === this.formData.category_type);
      }

      return categories;
    },
  },
  methods: {
    open() {
      this.$root.$emit('bv::show::modal', 'admin-category-image-add-modal');
    },
    close() {
      this.$root.$emit('bv::hide::modal', 'admin-category-image-add-modal');
    },
    setCategoryType(categoryTypeId) {
      this.formData.category_type = categoryTypeId;
    },
    setCategory(categoryId) {
      this.formData.category = categoryId;
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    submitForm() {
      if (this.formState.sizeExceeds) {
        this.$bvToast.toast(`Total size of your images should not exceed ${this.maxFilesSize} MiB`, {
          title: 'Error!',
          variant: 'danger'
        });

        return;
      }

      this.formState.processing = true;

      let formData = new FormData();
      formData.append("category", this.formData.category);
      if (this.formData.images) {
        this.formData.images.forEach(function (file) {
          formData.append("images[]", file, file.name);
        })
      }

      this.$api.post(API_ROUTES.ADMIN_TAXONOMY_CATEGORY_IMAGES, formData).then(({data}) => {
        this.formValidation = {};
        this.$emit('success', data.category_images);
        this.formData.images = null;
        this.close();
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to create a new category image', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            let error = data.errors[fieldName].join(', ');
            if (fieldName.indexOf('images.') === 0) {
              error = error.replace(fieldName, 'image');
              validationErrors['images'] = error;
            }else {
              validationErrors[fieldName] = error;
            }
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  },
  watch: {
    "formData.category_type": function (categoryTypeId) {
      if (this.formData.category) {
        let category = this.categories.find(c => c.value === this.formData.category);
        if (!category || category.category_type_id !== categoryTypeId) {
          this.formData.category = "";
        }
      }
    },
    "formData.category": function (categoryId) {
      let category = this.categories.find(c => c.value === categoryId);
      if (category && this.formData.category_type !== category.category_type_id) {
        this.formData.category_type = category.category_type_id;
      }
    },
    "formData.images": function (images) {
      let totalSize = 0;
      if (images.length > 0) {
        totalSize = images.map(file => file.size).reduce((a, b) => a + b, 0) / 1024 / 1024; //in MiB
      }
      this.formState.sizeExceeds = totalSize.toFixed(2) > this.maxFilesSize;
      if (this.formState.sizeExceeds) {
        this.formValidation['images'] = `Total size of your images should not exceed ${this.maxFilesSize} MiB`;
      } else {
        delete this.formValidation['images'];
      }
    }
  }
}
</script>
